/* eslint-disable react/no-danger */
import { blueGrey, grey } from "@mui/material/colors";
import {
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  GKCheckbox,
  GKDatePicker,
  GKEditor,
  GKRadio,
  GKSearchSelect,
  GKTextField,
  Grid2,
  RadioGroup,
  Typography,
} from "components";
import GKImageUploader from "components/GKImageUploader";
import dayjs from "dayjs";
import { EquitybasketFormInterface } from "interfaces/EquityBasket";
import { isEmpty } from "lodash";
import { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";
import axiosInstance from "utils/axiosInstance";
import { INVESTMENT_STRATEGIES, minimumInvestmentData } from "utils/constants";
import { refetchQuery } from "utils/helpers";
import {
  assetClassType,
  backetCategoriesData,
  basketTypeDropdown,
  comparableIndexData,
  subscriptionDropdown,
  timePerdioDropdown,
  volatilityDropdown,
} from "../equityBasketHelper";
import { useMutation } from "hooks/useQueries";

const MetaTab = ({
  riskProfileDataLoading,
  riskProfileData,
  companyMasterData,
}: {
  riskProfileDataLoading: boolean;
  companyMasterData: any;
  riskProfileData: any;
}) => {
  const { id } = useParams();

  const form = useFormContext<EquitybasketFormInterface>();

  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const [croppedImage, setCroppedImage] = useState<string | null>(null);

  const basketCategoryWatch = form.watch("basketCategory");

  /** ========================================================================================================= */
  const { mutate: handleUpdateEquityBasket, isLoading: updateLoading } =
    useMutation({
      mutationFn: (data: any) =>
        axiosInstance.patch(`/model_portfolio/${id}/`, data),
      onSuccess: () => {
        refetchQuery("GET_MODEL_PORTFOLIO_DETAILS_DATA");
        refetchQuery("EQUITY_BASKET_FEE_PLAN_HISTORY");
        form.setValue("researchFiles", []);
      },
    });

  return (
    <Grid2 container spacing={7}>
      <Grid2 size={8} container spacing={3}>
        <Grid2 size={6}>
          <Controller
            name="managementStyle"
            control={form.control}
            rules={{
              required: {
                value: true,
                message: "Select Advisor Types",
              },
            }}
            render={({ field, fieldState: { error } }) => {
              return (
                <GKSearchSelect
                  {...field}
                  requiredField
                  disabled={!isEmpty(field.value)}
                  onChange={(e: any, val) => field.onChange(val)}
                  disableClearable={false}
                  value={field.value}
                  options={subscriptionDropdown}
                  inputLabel="Select Advisor Types"
                  error={!!error}
                  helperText={error?.message}
                />
              );
            }}
          />
        </Grid2>
        <Grid2 size={6}>
          <Controller
            name="basketType"
            control={form.control}
            rules={{
              required: {
                value: true,
                message: "Select basket type",
              },
            }}
            render={({ field, fieldState: { error } }) => {
              return (
                <GKSearchSelect
                  {...field}
                  onChange={(e, val) => field.onChange(val?.value)}
                  requiredField
                  disableClearable={false}
                  disabled={!isEmpty(field.value)}
                  value={
                    basketTypeDropdown?.find(
                      (item) => item?.value === field.value,
                    )?.name || []
                  }
                  options={basketTypeDropdown}
                  getOptionLabel={(opt) => opt?.name || opt}
                  inputLabel="Basket Type"
                  error={!!error}
                  helperText={error?.message}
                />
              );
            }}
          />
        </Grid2>
        <Grid2 size={6}>
          <Controller
            name="basketCategory"
            control={form.control}
            render={({ field, fieldState: { error } }) => {
              return (
                <GKSearchSelect
                  {...field}
                  requiredField
                  onChange={(e: any, val) => field.onChange(val.value)}
                  disableClearable={false}
                  getOptionLabel={(option) => option.name || option}
                  value={
                    backetCategoriesData.find((d) => d.value === field?.value)
                      ?.name
                  }
                  options={backetCategoriesData}
                  inputLabel="Select Basket Category"
                  error={!!error}
                  helperText={error?.message}
                />
              );
            }}
          />
        </Grid2>
        <Grid2 size={6}>
          <Controller
            name="code"
            control={form.control}
            rules={{
              required: {
                value: true,
                message: "Please Enter Name",
              },
            }}
            render={({ field, fieldState: { error } }) => {
              return (
                <GKTextField
                  {...field}
                  requiredField
                  inputLabel="Basket Code"
                  placeholder="Basket Code"
                  error={!!error}
                  helperText={error?.message}
                />
              );
            }}
          />
        </Grid2>
        <Grid2 size={12}>
          <Controller
            rules={{
              required: {
                value: true,
                message: "Please Enter Name",
              },
            }}
            name="name"
            control={form.control}
            render={({ field, fieldState: { error } }) => {
              return (
                <GKTextField
                  {...field}
                  requiredField
                  inputLabel="Basket Name"
                  placeholder="Basket Name"
                  error={!!error}
                  helperText={error?.message}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <Typography
                          color={grey[600]}
                          width={"-webkit-fill-available"}
                          textAlign={"right"}
                        >
                          {basketCategoryWatch || "Basket Category"}
                        </Typography>
                      ),
                    },
                  }}
                />
              );
            }}
          />
        </Grid2>
        <Grid2 size={6}>
          <Controller
            name="basketCreationDate"
            control={form.control}
            render={({ field }) => (
              <GKDatePicker
                {...field}
                inputLabel="Basket Creation Date"
                value={dayjs(field.value)}
                onChange={(value) => {
                  field.onChange(dayjs(value).format("YYYY-MM-DD"));
                }}
                slotProps={{
                  textField: {
                    error: false,
                    helperText: false,
                  },
                }}
              />
            )}
          />
        </Grid2>
        <Grid2 size={6}>
          <Controller
            name="managedBy"
            control={form.control}
            render={({ field }) => {
              return (
                <GKTextField
                  {...field}
                  inputLabel="Managed By"
                  placeholder="Managed By"
                />
              );
            }}
          />
        </Grid2>
        <Grid2 size={12}>
          <Controller
            name="overview"
            control={form.control}
            render={({ field }) => {
              return (
                <GKEditor
                  {...field}
                  value={field.value || ""}
                  inputLabel="Overview"
                  toolbar={[
                    "undo",
                    "redo",
                    "|",
                    "heading",
                    "bold",
                    "italic",
                    "underline",
                    "strikethrough",
                    "|",
                    "fontColor",
                    "fontBackgroundColor",
                    "|",
                    "alignment",
                    "link",
                    "|",
                    "bulletedList",
                    "numberedList",
                  ]}
                />
              );
            }}
          />
        </Grid2>
        <Grid2 size={12}>
          <Typography fontWeight={600}>Investment Criteria</Typography>
        </Grid2>
        <Grid2 size={6}>
          <Controller
            name="riskProfile"
            control={form.control}
            rules={{
              required: {
                value: true,
                message: "Select risk profile",
              },
            }}
            render={({ field, fieldState: { error } }) => {
              return (
                <GKSearchSelect
                  {...field}
                  requiredField
                  loading={riskProfileDataLoading}
                  value={field?.value[0]}
                  onChange={(e, val) => field.onChange([val])}
                  options={riskProfileData?.data?.data?.data?.pointsDetails}
                  disableClearable={false}
                  inputLabel="Risk Profile"
                  error={!!error}
                  helperText={error?.message}
                />
              );
            }}
          />
        </Grid2>
        <Grid2 size={6}>
          <Controller
            name="volatility"
            control={form.control}
            render={({ field }) => {
              return (
                <GKSearchSelect
                  {...field}
                  onChange={(e, val) => {
                    field.onChange(val?.value);
                  }}
                  value={
                    volatilityDropdown?.find((da) => da?.value === field?.value)
                      ?.name || []
                  }
                  options={volatilityDropdown}
                  disableClearable={false}
                  getOptionLabel={(opt) => opt?.name || opt}
                  inputLabel="Volatility"
                />
              );
            }}
          />
        </Grid2>
        <Grid2 size={6}>
          <Controller
            name="timePeriod"
            control={form.control}
            render={({ field }) => {
              return (
                <GKSearchSelect
                  {...field}
                  onChange={(e, val) => {
                    field.onChange(val?.value);
                  }}
                  value={
                    timePerdioDropdown?.find((da) => da?.value === field?.value)
                      ?.name || []
                  }
                  options={timePerdioDropdown}
                  disableClearable={false}
                  getOptionLabel={(opt) => opt?.name || opt}
                  inputLabel="Time Horizon"
                />
              );
            }}
          />
        </Grid2>
        <Grid2 size={6}>
          <Controller
            name="minimumInvestmentValueCheck"
            control={form.control}
            render={({ field }) => {
              return (
                <GKSearchSelect
                  {...field}
                  onChange={(e, val) => {
                    field.onChange(val?.value);
                  }}
                  value={
                    minimumInvestmentData?.find(
                      (da) => da?.value === `${field?.value}`,
                    )?.name || []
                  }
                  options={minimumInvestmentData}
                  disableClearable={false}
                  getOptionLabel={(opt) => opt?.name || opt}
                  inputLabel={"Minimum Investment Amount"}
                  tooltipText="The minimum amount a subscriber can invest after subscribing to the basket."
                />
              );
            }}
          />
        </Grid2>
        <Grid2 size={12}>
          <Typography fontWeight={600}>
            Asset Allocation & Benchmark details
          </Typography>
        </Grid2>
        <Grid2 size={12}>
          <Controller
            name="constituentsType"
            control={form.control}
            render={({ field }) => {
              return (
                <FormControl>
                  <FormLabel>Constituents Type</FormLabel>
                  <RadioGroup {...field}>
                    <FormControlLabel
                      value="Stocks & ETFs"
                      control={
                        <GKRadio
                          checked
                          disableLeftPadding
                          value={"Stocks & ETFs"}
                        />
                      }
                      label="Stocks & ETFs"
                    />
                  </RadioGroup>
                </FormControl>
              );
            }}
          />{" "}
        </Grid2>
        <Grid2 size={6}>
          <Controller
            name="assetClassType"
            control={form.control}
            render={({ field }) => {
              return (
                <GKSearchSelect
                  {...field}
                  onChange={(e, val) => {
                    field.onChange(val?.value);
                  }}
                  value={
                    assetClassType?.find(
                      (da) => da?.value === `${field?.value}`,
                    )?.name || []
                  }
                  options={assetClassType}
                  disableClearable={false}
                  getOptionLabel={(opt) => opt?.name || opt}
                  inputLabel={"Asset Class Type"}
                  tooltipModal={{
                    isTrue: true,
                    tooltipModelLabel: "Asset Class Type",
                    tooltipModalContent: (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: `<p><span style="background-color:transparent;color:#000000;"><strong>The specific constituents of each portfolio are selected from a universe defined by the managers. This constituent universe is labelled as Asset Class.</strong></span></p><ul><li><span style="background-color:transparent;color:#000000;"><strong>If the asset class is Equity Large Cap, then all underlying portfolio constituents are selected from the Large Cap segment representing top 100 companies by market capitalisation listed on the NSE (National Stock Exchange of India).</strong></span></li><li><span style="background-color:transparent;color:#000000;"><strong>If the asset class is Equity Mid Cap, then all underlying portfolio constituents are selected from the Mid Cap segment representing the companies ranked 101 to 250 by market capitalisation listed on the NSE (National Stock Exchange of India).</strong></span></li><li><span style="background-color:transparent;color:#000000;"><strong>If the asset class is Equity Large &amp; Mid Cap, then all underlying portfolio constituents are selected from the Large &amp; Mid Cap segment representing the top 250 companies by market capitalisation listed on the NSE (National Stock Exchange of India).</strong></span></li><li><span style="background-color:transparent;color:#000000;"><strong>If the asset class is Equity Small Cap, then all underlying portfolio constituents are selected from the Small Cap segment representing the companies ranked greater than 251 by market capitalisation listed on the NSE (National Stock Exchange of India).</strong></span></li><li><span style="background-color:transparent;color:#000000;"><strong>If the asset class is Equity Mid &amp; Small Cap, then all underlying portfolio constituents are selected from the Mid &amp; Small Cap segment representing the companies ranked greater than 100 by market capitalisation listed on the NSE (National Stock Exchange of India).</strong></span></li><li><span style="background-color:transparent;color:#000000;"><strong>If the asset class is Equity All Cap, then all the underlying portfolio constituents are selected from Multi Cap segment which may include companies from more than two of the Large Cap, Mid Cap, Small Cap categories as described above.</strong></span></li><li><span style="background-color:transparent;color:#000000;"><strong>If the asset class is Debt, then all the underlying portfolio constituents are Debt instruments.</strong></span></li><li><span style="background-color:transparent;color:#000000;"><strong>If the asset class is Commodity, then all the underlying portfolio constituents are Commodity instruments.</strong></span></li><li><span style="background-color:transparent;color:#000000;"><strong>If the asset class is Commodity &amp; Debt, then all the underlying portfolio constituents are either Debt or Commodity instruments.</strong></span></li></ul>`,
                        }}
                      />
                    ),
                  }}
                />
              );
            }}
          />
        </Grid2>
        <Grid2 size={6}>
          <Controller
            name="comparableIndex"
            control={form.control}
            render={({ field }) => {
              return (
                <GKSearchSelect
                  {...field}
                  onChange={(e, val) => {
                    field.onChange(val?.value);
                  }}
                  value={
                    comparableIndexData?.find(
                      (da) => da?.value === `${field?.value}`,
                    )?.name || []
                  }
                  options={comparableIndexData}
                  disableClearable={false}
                  getOptionLabel={(opt) => opt?.name || opt}
                  inputLabel={"Comparable Index"}
                  tooltipModal={{
                    isTrue: true,
                    tooltipModelLabel: "Comparable Index",
                    tooltipModalContent: (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: `<p style="text-align:justify;"><span style="background-color:transparent;color:#000000;"><strong>For each baskets, currently a comparable index is determined on the basis of its holdings distribution. This remains unchanged and has no correlation with the new asset class metric being added. The logic for determining the same is provided below:</strong></span></p><p><span style="background-color:transparent;color:#183247;"><strong>&nbsp;</strong></span></p><ul><li><p style="text-align:justify;"><span style="background-color:transparent;color:#000000;"><strong>If the sum of weights of gold, silver, commodity, debt or REIT/INVIT constituents is greater than 50%, then comparable index is determined as Equity Multi-Cap</strong></span></p></li><li><p style="text-align:justify;"><span style="background-color:transparent;color:#000000;"><strong>If the sum of weights of large cap constituents is greater than 50%, then comparable index is determined as Equity Large Cap</strong></span></p></li><li><p style="text-align:justify;"><span style="background-color:transparent;color:#000000;"><strong>If the sum of weights of mid cap constituents is greater than 50%, then comparable index is determined as Equity Midcap</strong></span></p></li><li><p style="text-align:justify;"><span style="background-color:transparent;color:#000000;"><strong>If the sum of weights of small cap constituents is greater than 50%, then comparable index is determined as Equity Smallcap</strong></span></p></li><li><p style="text-align:justify;"><span style="background-color:transparent;color:#000000;"><strong>If the sum of weights of large cap constituents is greater than 30%, sum of weights of mid cap constituents are greater than 30%, and sum of weights of large cap and mid cap constituents are greater than 80%, then comparable index is determined as Equity Large &amp; Midcap</strong></span></p></li><li><p style="text-align:justify;"><span style="background-color:transparent;color:#000000;"><strong>If the sum of weights of small cap constituents is greater than 30%, sum of weights of mid cap constituents are greater than 30%, and sum of weights of small cap and mid cap constituents are greater than 80%, then comparable index is determined as Equity Mid and Smallcap</strong></span></p></li><li><p style="text-align:justify;"><span style="background-color:transparent;color:#000000;"><strong>If none of the above conditions are met, then comparable index is determined as Equity Multi-cap</strong></span></p></li></ul>`,
                        }}
                      />
                    ),
                  }}
                />
              );
            }}
          />
        </Grid2>
        <Grid2 size={12}>
          <Controller
            name="benchmark"
            control={form.control}
            render={({ field }) => {
              return (
                <GKSearchSelect
                  {...field}
                  onChange={(e, val) => {
                    field.onChange(val?.value);
                  }}
                  value={
                    companyMasterData?.data?.data?.find(
                      (item: any) => item?.gridCode === field.value,
                    )?.companyName || []
                  }
                  options={companyMasterData?.data?.data?.map((item: any) => ({
                    ...item,
                    name: item?.companyName,
                    value: item?.gridCode,
                  }))}
                  getOptionLabel={(opt) => opt?.name || opt}
                  disableClearable={false}
                  inputLabel="Benchmark"
                />
              );
            }}
          />
        </Grid2>
        <Grid2 size={12}>
          <Typography color={"var(--black)"}>
            Investment Strategies{" "}
            <span
              style={{
                color: "var(--neutral-500)",
                fontSize: 12,
              }}
            >
              (Select upto 5)
            </span>
          </Typography>
          <Typography fontSize={10} color={blueGrey[500]} my={1}>
            Note: Users can find your basket faster when searching for specific
            themes.
          </Typography>
          <Controller
            name="investmentStyle"
            control={form.control}
            render={({ field }) => (
              <FormGroup>
                <Grid2 container spacing={1}>
                  {INVESTMENT_STRATEGIES?.map((dataItem) => (
                    <Grid2 size={6} key={dataItem.value}>
                      <FormControlLabel
                        key={dataItem.value}
                        control={
                          <GKCheckbox
                            checked={field.value?.includes(dataItem.value)}
                            disabled={
                              field.value?.length === 5 &&
                              !field.value?.includes(dataItem.value)
                            }
                            onChange={() => {
                              const currentValue =
                                form.getValues("investmentStyle") || [];
                              const updatedOptions = currentValue.includes(
                                dataItem.value,
                              )
                                ? currentValue.filter(
                                    (item: any) => item !== dataItem.value,
                                  )
                                : [...currentValue, dataItem.value];
                              field.onChange(updatedOptions);
                            }}
                          />
                        }
                        label={dataItem.name}
                      />
                    </Grid2>
                  ))}
                </Grid2>
              </FormGroup>
            )}
          />
        </Grid2>
      </Grid2>
      <Grid2 size={4}>
        <Controller
          name="basketLogo"
          control={form.control}
          render={() => (
            <GKImageUploader
              croppedImage={croppedImage}
              deleteLoading={updateLoading}
              handleProfilePicDelete={() => {
                const formData = new FormData();
                formData.append("basketLogo", null);
                handleUpdateEquityBasket(formData);
              }}
              selectedImage={selectedImage || form.getValues("basketLogo")}
              setCroppedImage={setCroppedImage}
              setSelectedImage={setSelectedImage}
              form={form}
              logoName={"basketLogo"}
              buttonName="Upload Basket Logo"
            />
          )}
        />
      </Grid2>
    </Grid2>
  );
};

export default MetaTab;
